import { useEffect, useMemo, useRef } from 'react';
import { AccountBaselineStatusResponse, AwsAccount, RoleValidationResult } from '../../../types/backend';
import { AWSAccountConfig } from '../../../types';
import { useAwsAccountService } from '../services/awsAccountService';
import { useEntityManagement, EntityCrudService } from '../../../shared/hooks/useEntityManagement';

export interface AccountFormData {
  aws_account_id: string;
  account_name: string;
  read_role_name: string;
  write_role_name: string;
  automation_role_name: string;
  /** The seed role the customer installs. Ours only in the sense that we assume it. */
  onboarding_role_name: string;
  external_id: string;
  main_region: string;
  supported_regions: string[];
  event_bus_name: string;
  /** A disabled account is skipped entirely, so nothing onboards it. */
  enabled: boolean;
  /** Whether OpsPilot installs this account's baselines itself, rather than
      leaving them with Service Catalog. Off until the account is migrated.
      Null when the decision could not be read: unknown, never off. */
  baseline_managed: boolean | null;
  /** Whether Cloud2 stays read-only here until the customer breaks glass in
      their own tenant. Decided when the roles are first installed. Null when
      the decision could not be read. */
  break_glass_required: boolean | null;
}

/**
 * A switch's value when a save must send it, or null when it did not move.
 *
 * Both of these travel on their own action, so sending one whenever the form
 * carries it would put an extra call behind every account edit: the value is on
 * the form whether or not the operator touched it.
 */
export function decisionToSend(
  requested: boolean | undefined,
  saved: boolean | undefined
): boolean | null {
  if (requested === undefined) return null;
  return requested === (saved ?? false) ? null : requested;
}

/**
 * Whether the form differs from the saved account.
 *
 * Every editable field belongs here. A field missing from this comparison is
 * not a cosmetic bug: Save is gated on it, so the control renders, toggles and
 * cannot be saved.
 *
 * Exported so it can be tested field by field, which is the only way this
 * stays exhaustive as fields are added.
 */
/** The subset of the account service these writes need. */
export interface AccountWriteService {
  getCustomerAwsAccounts: (customerId: string) => Promise<{ customer_accounts: AwsAccount[] }>;
  getAccountBaselineStatus: (customerId: string) => Promise<AccountBaselineStatusResponse>;
  createCustomerAwsAccount: (customerId: string, dto: AWSAccountConfig) => Promise<AwsAccount>;
  updateCustomerAwsAccount: (customerId: string, id: string, dto: Partial<AWSAccountConfig>) => Promise<AwsAccount>;
  setAccountBaselineManaged: (customerId: string, id: string, managed: boolean) => Promise<unknown>;
  setAccountBreakGlass: (customerId: string, id: string, required: boolean) => Promise<unknown>;
}

/**
 * Reading and writing an account, with the two decisions on their own calls.
 *
 * Both say something about the customer's own AWS account rather than about
 * OpsPilot's record of it: `baseline_managed` makes the hourly loop install
 * CloudFormation there, `break_glass_required` decides whether
 * cloud2-operator-admin is clamped to read-only. Each has its own action, so
 * neither can ride along inside an ordinary edit. They go first: if the caller
 * may not make a decision, nothing else is saved either.
 *
 * `saved` is the last-loaded pair per account, mutated in place, so a save
 * sends a decision only when its switch actually moved.
 */
export interface SavedDecisions {
  baseline_managed: boolean;
  break_glass_required: boolean;
}

/**
 * An account as this form edits it: customer_management's record joined to
 * account_baseline's two decisions about it, which live on their own record.
 *
 * When account_baseline could not be read, both decisions are null and
 * `decisions_error` says why. Null is unknown, not off: reading it as off
 * would offer to switch on a decision that may already be on.
 */
export type AccountEntity = AwsAccount & {
  baseline_managed: boolean | null;
  break_glass_required: boolean | null;
  decisions_error?: string;
};

/** An account whose decisions could not be read, carrying the reason. */
export function withUnknownDecisions(account: AwsAccount, reason: string): AccountEntity {
  return {
    ...account,
    baseline_managed: null,
    break_glass_required: null,
    decisions_error: reason,
  };
}

/**
 * Join each account to its decisions from the baseline status.
 *
 * The status lists every account of the customer, with an undecided one
 * reading off on both, so an account it does not list means the two reads
 * disagree about what exists. That raises rather than reading as off: off
 * would offer to switch on a decision that may already be on.
 */
export function withDecisions(
  accounts: AwsAccount[],
  status: AccountBaselineStatusResponse
): AccountEntity[] {
  const byAccount = new Map(status.accounts.map(v => [v.aws_account_id, v]));
  return accounts.map(account => {
    const view = byAccount.get(account.aws_account_id);
    if (!view) {
      throw new Error(
        `No baseline decisions were returned for account ${account.aws_account_id}`
      );
    }
    // The backend always sends both; they are optional in the generated type only
    // because the model defaults them to false, its rule for an undecided account.
    return {
      ...account,
      baseline_managed: view.baseline_managed ?? false,
      break_glass_required: view.break_glass_required ?? false,
    };
  });
}

/**
 * Record an account's decisions as last loaded from the server.
 *
 * Called from both places an account arrives: the list, and selecting one to
 * edit. Selecting is the one that always runs for the account being saved, so a
 * fresh mount reading a warm cache still knows what was stored. Without it an
 * unknown value reads as off, every save looks like the switch moved, and an
 * operator who never touched it is refused.
 *
 * An account whose decisions could not be read leaves no entry, so a save
 * sends neither decision for it.
 */
export function rememberDecisions(
  saved: Record<string, SavedDecisions>,
  account: AccountEntity
): void {
  if (account.baseline_managed === null || account.break_glass_required === null) {
    delete saved[account.id];
    return;
  }
  saved[account.id] = {
    baseline_managed: account.baseline_managed,
    break_glass_required: account.break_glass_required,
  };
}

const errorMessage = (cause: unknown) =>
  cause instanceof Error ? cause.message : String(cause);

export function accountWrites(
  service: AccountWriteService,
  customerId: string,
  saved: Record<string, SavedDecisions>
) {
  const remember = (id: string, patch: Partial<SavedDecisions>) => {
    saved[id] = { ...{ baseline_managed: false, break_glass_required: false }, ...saved[id], ...patch };
  };

  return {
    getAll: async (): Promise<AccountEntity[]> => {
      // The accounts load on their own: a failed decisions read must not take
      // the account list down with it. It marks the decisions unknown instead.
      const [listed, status] = await Promise.all([
        service.getCustomerAwsAccounts(customerId),
        service.getAccountBaselineStatus(customerId).then(
          (read) => ({ read }),
          (cause: unknown) => ({ cause }),
        ),
      ]);
      const accounts = 'read' in status
        ? withDecisions(listed.customer_accounts, status.read)
        : listed.customer_accounts.map(a => withUnknownDecisions(
          a, `The baseline decisions could not be read: ${errorMessage(status.cause)}`
        ));
      Object.keys(saved).forEach(k => delete saved[k]);
      accounts.forEach(a => rememberDecisions(saved, a));
      return accounts;
    },
    create: async (dto: AWSAccountConfig): Promise<AccountEntity> => {
      const { baseline_managed, break_glass_required, ...rest } = dto;
      const created = await service.createCustomerAwsAccount(customerId, rest);
      // Only a yes is a decision worth a call. The form always carries both
      // fields, so sending them whenever present would put two extra calls
      // behind every registration.
      //
      // The account exists by this point, so a decision that fails here cannot
      // be reported as "registration failed": the admin would believe nothing
      // happened while the account sits there without the setting they asked
      // for, which for break-glass means Cloud2 keeps standing admin in a
      // customer account that was supposed to clamp it. Say exactly that.
      const decisions: Array<[boolean | undefined, string, () => Promise<unknown>]> = [
        [break_glass_required, 'Customer controls our admin access',
          () => service.setAccountBreakGlass(customerId, created.id, true)],
        [baseline_managed, 'OpsPilot installs the baselines',
          () => service.setAccountBaselineManaged(customerId, created.id, true)],
      ];
      for (const [wanted, label, call] of decisions) {
        if (wanted !== true) continue;
        try {
          await call();
        } catch (cause) {
          throw Object.assign(
            new Error(
              `The account was registered, but "${label}" was not switched on: ` +
              `${errorMessage(cause)}. ` +
              `Open the account and switch it on.`
            ),
            { cause }
          );
        }
      }
      const decided: SavedDecisions = {
        break_glass_required: break_glass_required === true,
        baseline_managed: baseline_managed === true,
      };
      remember(created.id, decided);
      return { ...created, ...decided };
    },
    update: async (id: string, dto: Partial<AWSAccountConfig>): Promise<AccountEntity> => {
      const { baseline_managed, break_glass_required, ...rest } = dto;
      if (!saved[id]) {
        // Unknown decisions travel as absent (the form sends a null as
        // undefined). A value here with nothing loaded to compare against
        // cannot be told apart from a moved switch, so refuse before any write.
        if (baseline_managed !== undefined || break_glass_required !== undefined) {
          throw new Error(
            `The decisions for account ${id} were never loaded, so they cannot be saved`
          );
        }
        const updated = await service.updateCustomerAwsAccount(customerId, id, rest);
        return withUnknownDecisions(updated, 'The baseline decisions were not loaded');
      }
      const glass = decisionToSend(break_glass_required, saved[id]?.break_glass_required);
      if (glass !== null) {
        await service.setAccountBreakGlass(customerId, id, glass);
        remember(id, { break_glass_required: glass });
      }
      const managed = decisionToSend(baseline_managed, saved[id]?.baseline_managed);
      if (managed !== null) {
        await service.setAccountBaselineManaged(customerId, id, managed);
        remember(id, { baseline_managed: managed });
      }
      const updated = await service.updateCustomerAwsAccount(customerId, id, rest);
      return { ...updated, ...saved[id] };
    },
  };
}

/** The two role names that point at an administrator role. '' is no role. */
export interface AdminRoles {
  automation_role_name: string;
  onboarding_role_name: string;
}

const ADMIN_ROLE_FIELDS: Array<keyof AdminRoles> = ['automation_role_name', 'onboarding_role_name'];

/**
 * The seed role the customer installs by hand (account-onboarding-role.yml).
 * The server stores it on every new account, so the form starts from it.
 */
export const ONBOARDING_ROLE_NAME = 'cloud2-onboarding';

/**
 * What the form saves: the account fields plus the two admin role names.
 * Only withAdminRoles takes it; it hands create and update an AWSAccountConfig,
 * whose type refuses both names.
 */
export type AccountFormDto = Omit<AWSAccountConfig, keyof AdminRoles> & Partial<AdminRoles>;

/** Record an account's two admin role names as last loaded from the server. */
export function rememberAdminRoles(
  saved: Record<string, AdminRoles>,
  account: { id: string; automation_role_name?: string | null; onboarding_role_name?: string | null }
): void {
  saved[account.id] = {
    automation_role_name: account.automation_role_name || '',
    onboarding_role_name: account.onboarding_role_name || '',
  };
}

/** The account DTO without the two admin role names, which never ride along in it. */
function splitAdminRoles<T extends Partial<AccountFormDto>>(dto: T): [Omit<T, keyof AdminRoles>, Partial<AdminRoles>] {
  const { automation_role_name, onboarding_role_name, ...rest } = dto;
  return [rest, { automation_role_name, onboarding_role_name }];
}

/** The named roles that differ from `saved`. */
function adminRolesToSend(
  requested: Partial<AdminRoles>,
  saved: AdminRoles | undefined
): Partial<AdminRoles> | null {
  const changes: Partial<AdminRoles> = {};
  for (const field of ADMIN_ROLE_FIELDS) {
    const value = requested[field];
    if (value && value !== (saved?.[field] ?? '')) changes[field] = value;
  }
  return Object.keys(changes).length ? changes : null;
}

/** The subset of the account service the admin role names need. */
export interface AdminRolesService {
  setAwsAccountAdminRoles: (customerId: string, id: string, roles: Partial<AdminRoles>) => Promise<unknown>;
}

interface AccountWrites<Dto> {
  getAll: () => Promise<AccountEntity[]>;
  create: (dto: Dto) => Promise<AccountEntity>;
  update: (id: string, dto: Partial<Dto>) => Promise<AccountEntity>;
}

/**
 * The account writes, with the automation and onboarding role names split off
 * onto customers.SetAWSAccountAdminRoles.
 *
 * Both name an administrator role Cloud2 assumes in the customer's account, so
 * the server takes them only on that admin action and refuses them inside
 * create and update. Below admin they are never sent; for an admin they are
 * sent only when they moved, before the rest of an edit, as the decisions are.
 */
export function withAdminRoles(
  writes: AccountWrites<AWSAccountConfig>,
  service: AdminRolesService,
  customerId: string,
  allowAdminRoles: boolean,
  saved: Record<string, AdminRoles>
): AccountWrites<AccountFormDto> {
  return {
    getAll: async () => {
      const accounts = await writes.getAll();
      Object.keys(saved).forEach(k => delete saved[k]);
      accounts.forEach(a => rememberAdminRoles(saved, a));
      return accounts;
    },
    create: async (dto) => {
      const [rest, roles] = splitAdminRoles(dto);
      const created = await writes.create(rest);
      // The server stores the fixed seed role on every registration, so the
      // admin call carries only what differs from the record it just wrote.
      rememberAdminRoles(saved, created);
      const changes = allowAdminRoles ? adminRolesToSend(roles, saved[created.id]) : null;
      if (changes === null) return created;
      try {
        await service.setAwsAccountAdminRoles(customerId, created.id, changes);
      } catch (cause) {
        throw Object.assign(
          new Error(
            `The account was registered, but its automation and onboarding roles were not set: ` +
            `${errorMessage(cause)}. Open the account and set them.`
          ),
          { cause }
        );
      }
      const withRoles = { ...created, ...changes };
      rememberAdminRoles(saved, withRoles);
      return withRoles;
    },
    update: async (id, dto) => {
      const [rest, roles] = splitAdminRoles(dto);
      const changes = allowAdminRoles ? adminRolesToSend(roles, saved[id]) : null;
      if (changes !== null) {
        await service.setAwsAccountAdminRoles(customerId, id, changes);
      }
      const updated = await writes.update(id, rest);
      rememberAdminRoles(saved, updated);
      return updated;
    },
  };
}

/** An account with no decisions part of the form: neither read nor in error. */
function withoutDecisions(account: AwsAccount): AccountEntity {
  return { ...account, baseline_managed: null, break_glass_required: null };
}

/**
 * Reading and writing an account while account_baseline is off.
 *
 * Every baseline action is refused then, so none is called: the accounts load
 * without the baseline status and save without either decision, whatever the
 * form carries for them.
 */
export function accountWritesWithoutBaseline(service: AccountWriteService, customerId: string) {
  const stripDecisions = <T extends Partial<AWSAccountConfig>>(dto: T): T => {
    const rest = { ...dto };
    delete rest.baseline_managed;
    delete rest.break_glass_required;
    return rest;
  };

  return {
    getAll: async (): Promise<AccountEntity[]> => {
      const listed = await service.getCustomerAwsAccounts(customerId);
      return listed.customer_accounts.map(withoutDecisions);
    },
    create: async (dto: AWSAccountConfig): Promise<AccountEntity> =>
      withoutDecisions(await service.createCustomerAwsAccount(customerId, stripDecisions(dto))),
    update: async (id: string, dto: Partial<AWSAccountConfig>): Promise<AccountEntity> =>
      withoutDecisions(await service.updateCustomerAwsAccount(customerId, id, stripDecisions(dto))),
  };
}

export function accountHasChanges(
  form: AccountFormData,
  account: AccountEntity | null,
  isNew: boolean
): boolean {
  if (isNew) return true;
  if (!account) return false;
  return form.aws_account_id !== account.aws_account_id ||
    form.account_name !== account.account_name ||
    form.read_role_name !== account.read_role_name ||
    form.write_role_name !== (account.write_role_name || '') ||
    form.automation_role_name !== (account.automation_role_name || '') ||
    form.onboarding_role_name !== (account.onboarding_role_name || '') ||
    form.external_id !== (account.external_id || '') ||
    form.main_region !== account.main_region ||
    form.event_bus_name !== (account.event_bus_name || 'cloud2-events') ||
    form.enabled !== account.enabled ||
    form.baseline_managed !== account.baseline_managed ||
    form.break_glass_required !== account.break_glass_required ||
    JSON.stringify(form.supported_regions.sort()) !== JSON.stringify(account.supported_regions.sort());
}

const EMPTY_FORM: AccountFormData = {
  aws_account_id: '',
  account_name: '',
  read_role_name: 'cloud2-operator-readonly',
  write_role_name: 'cloud2-operator-admin',
  automation_role_name: '',
  onboarding_role_name: ONBOARDING_ROLE_NAME,
  external_id: '',
  main_region: 'eu-central-1',
  supported_regions: ['eu-central-1'],
  event_bus_name: 'cloud2-events',
  enabled: true,
  baseline_managed: false,
  break_glass_required: false
};

export const getAccountIdError = (accountId: string, accounts: AwsAccount[], selectedId?: string): string | null => {
  if (!accountId) return null;
  if (!/^\d+$/.test(accountId)) return "Must contain only numbers";
  if (accountId.length < 12) return "Must be exactly 12 digits";
  const isDuplicate = accounts.some(acc =>
    acc.aws_account_id === accountId && acc.id !== selectedId
  );
  if (isDuplicate) return "This account ID is already configured";
  return null;
};

export const getRoleNameError = (roleName: string): string | null => {
  if (!roleName) return null;
  if (!/^[\w+=,.@-]+$/.test(roleName)) {
    return "Invalid role name format (allowed: alphanumeric, +=,.@-)";
  }
  if (roleName.length > 64) return "Must be 64 characters or less";
  return null;
};

interface UseAccountFormOptions {
  customerId: string;
  isOpen: boolean;
  /** Whether account_baseline is on. Off, the form never reads or writes the
      two decisions, since every baseline action is refused. */
  baselineEnabled: boolean;
  /** Whether the caller is admin on this customer. Below admin the automation
      and onboarding role names are never sent: only an admin may set them. */
  allowAdminRoles: boolean;
}

export function useAccountForm({ customerId, isOpen, baselineEnabled, allowAdminRoles }: UseAccountFormOptions) {
  const awsAccountService = useAwsAccountService();

  // `enabled` is on the update request only; create has no such field and the
  // backend forbids extras, so sending it on create would fail the call.
  const formToDto = (form: AccountFormData) => ({
    customer_id: customerId,
    aws_account_id: form.aws_account_id,
    account_name: form.account_name,
    read_role_name: form.read_role_name,
    write_role_name: form.write_role_name || undefined,
    automation_role_name: form.automation_role_name || undefined,
    onboarding_role_name: form.onboarding_role_name || undefined,
    external_id: form.external_id || undefined,
    main_region: form.main_region,
    supported_regions: form.supported_regions,
    event_bus_name: form.event_bus_name || undefined,
    // An unknown decision (null) is not sent at all, never as off.
    baseline_managed: form.baseline_managed ?? undefined,
    break_glass_required: form.break_glass_required ?? undefined
  });

  const formToUpdateDto = (form: AccountFormData) => ({
    ...formToDto(form),
    enabled: form.enabled,
  });

  // Break-glass as last loaded, so a save knows whether the switch moved.
  // Sending it on every save would make every account edit an admin action.
  const savedDecisions = useRef<Record<string, SavedDecisions>>({});
  // The two admin role names as last loaded, so a save sends them only when they moved.
  const savedAdminRoles = useRef<Record<string, AdminRoles>>({});

  const crudService = useMemo((): EntityCrudService<AccountEntity, AccountFormDto, Partial<AccountFormDto>> => ({
    ...withAdminRoles(
      baselineEnabled
        ? accountWrites(awsAccountService, customerId, savedDecisions.current)
        : accountWritesWithoutBaseline(awsAccountService, customerId),
      awsAccountService, customerId, allowAdminRoles, savedAdminRoles.current
    ),
    delete: (id) => awsAccountService.deleteCustomerAwsAccount(customerId, id),
    testAccess: (id, roleType: 'readonly' | 'write' | 'automation', region: string) =>
      awsAccountService.testCustomerRoleAccess(customerId, id, roleType, region)
  }), [awsAccountService, customerId, baselineEnabled, allowAdminRoles]);

  const entityManagement = useEntityManagement<AccountEntity, AccountFormData>({
    service: crudService,
    // The switch is in the key so flipping it reloads the accounts with or
    // without their decisions, rather than serving the other state's list.
    queryKey: ['aws-account', 'accounts', customerId ?? null, baselineEnabled],
    isOpen,
    emptyForm: EMPTY_FORM,
    getEntityId: (account) => account.id,
    entityToForm: (account) => (
      rememberDecisions(savedDecisions.current, account),
      rememberAdminRoles(savedAdminRoles.current, account),
      {
      aws_account_id: account.aws_account_id,
      account_name: account.account_name,
      read_role_name: account.read_role_name,
      write_role_name: account.write_role_name || '',
      automation_role_name: account.automation_role_name || '',
      onboarding_role_name: account.onboarding_role_name || '',
      external_id: account.external_id || '',
      main_region: account.main_region,
      supported_regions: account.supported_regions || [],
      event_bus_name: account.event_bus_name || 'cloud2-events',
      enabled: account.enabled,
      baseline_managed: account.baseline_managed,
      break_glass_required: account.break_glass_required
    }),
    formToCreateDto: formToDto,
    formToUpdateDto,
    validateForm: (form) => ({
      aws_account_id: !form.aws_account_id || form.aws_account_id.length !== 12 || !/^\d{12}$/.test(form.aws_account_id)
        ? 'Must be a 12-digit AWS account ID' : null,
      account_name: !form.account_name?.trim() ? 'Account name is required' : null,
      read_role_name: !form.read_role_name?.trim() ? 'Read role is required' : getRoleNameError(form.read_role_name),
      write_role_name: form.write_role_name ? getRoleNameError(form.write_role_name) : null,
      automation_role_name: form.automation_role_name ? getRoleNameError(form.automation_role_name) : null,
      onboarding_role_name: form.onboarding_role_name ? getRoleNameError(form.onboarding_role_name) : null,
      supported_regions: !form.supported_regions?.length ? 'At least one region required' : null,
      main_region: !form.supported_regions?.includes(form.main_region) ? 'Main region must be in supported regions' : null,
    }),
    hasFormChanges: accountHasChanges
  });

  // The selection survives a reload of the list, so an account selected before
  // the decisions were read (the switch still resolving, or the status read
  // failing) keeps null decisions on its form. A null is never sent, so a
  // decision changed on that form never reached the server. Once the list
  // carries decisions for the selected account, take the account from there
  // and fill in the decisions still unknown on the form; every other field
  // stays as the operator typed it.
  const { entities, selectedEntity, isNew, refreshSelected } = entityManagement;
  useEffect(() => {
    if (isNew || !selectedEntity || selectedEntity.baseline_managed !== null) return;
    const fresh = entities.find(e => e.id === selectedEntity.id);
    if (!fresh || fresh.baseline_managed === null) return;
    refreshSelected(fresh, (typed, read) => ({
      ...typed,
      baseline_managed: typed.baseline_managed ?? read.baseline_managed,
      break_glass_required: typed.break_glass_required ?? read.break_glass_required,
    }));
  }, [entities, selectedEntity, isNew, refreshSelected]);

  return {
    entities: entityManagement.entities,
    loading: entityManagement.loading,
    error: entityManagement.error,
    selectedEntity: entityManagement.selectedEntity,
    formData: entityManagement.formData,
    saving: entityManagement.saving,
    isNew: entityManagement.isNew,
    showDeleteConfirm: entityManagement.showDeleteConfirm,
    successMessage: entityManagement.successMessage,
    testing: entityManagement.testing,
    testResult: entityManagement.testResult as RoleValidationResult | null,
    handleSelect: entityManagement.handleSelect,
    handleAddNew: entityManagement.handleAddNew,
    handleSave: entityManagement.handleSave,
    handleDeleteClick: entityManagement.handleDeleteClick,
    handleDeleteConfirm: entityManagement.handleDeleteConfirm,
    handleDeleteCancel: entityManagement.handleDeleteCancel,
    handleChange: entityManagement.handleChange,
    handleTestRole: entityManagement.handleTestRole,
    setError: entityManagement.setError,
    setSuccessMessage: entityManagement.setSuccessMessage,
    isFormValid: entityManagement.isFormValid,
    hasChanges: entityManagement.hasChanges,
    customerId,
  };
}
